/* eslint-disable max-lines-per-function */
import React, { useState, useLayoutEffect } from 'react';
import UseAxios from '../../../Api/http/http';
import PropTypes from 'prop-types';
import CommonTextField from '../../../Components/TextField/textField';
import CommonDatePicker from '../../../Components/Datepicker/datepicker';
import './addPerformanceCycle.scss';
import FormDialog from '../../../Components/Modal/modal';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import {
  addPerformancePeriod,
  updatePerformancePeriod,
} from '../../../Api/request/performance';
import {
  ADD_EDIT_DIALOG,
  ADD_EDIT_RESPONSE_MESSAGES,
  CANCEL_TEXT,
  SAVE,
  UPDATE,
  AUTHENTICATION_FAILED,
} from '../../../Constants/strings';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/en';
dayjs.extend(customParseFormat);

dayjs.locale({
  ...dayjs.Ls.en,
  formats: {
    ...dayjs.Ls.en.formats,
    LongDate: 'DD-MM-YYYY',
  },
});

const AddEditPerformanceCycleForm = ({
  modalOpen,
  handleClose,
  lastUpdatedBy,
  updatePerformanceCycleList,
  setRows,
  isEdit,
  isEditMode,
  data,
}) => {
  const [autoFocus, setAutoFocus] = useState(false);
  const [performanceYearError, setPerformanceYearError] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [performanceYearValue, setPerformanceYearValue] = useState('');
  const [startDateValue, setStartDateValue] = useState('');
  const [endDateValue, setEndDateValue] = useState('');
  const { snackbarOpen } = useSnackbar();
  const currentDate = new Date();

  useLayoutEffect(() => {
    if (isEdit && modalOpen) {
      setPerformanceYearValue(data?.cycleName);
      setStartDateValue(data?.startDate);
      setEndDateValue(data?.endDate);
    }
  }, [data, isEdit, modalOpen]);

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  const resetValues = () => {
    if (!isEdit) {
      setPerformanceYearValue('');
      setStartDateValue('');
      setEndDateValue('');
    }
    setPerformanceYearError('');
    setStartDateError('');
    setEndDateError('');
  };

  // add/edit handleResponse for different response status
  const handleResponse = (responseData) => {
    switch (
      responseData.data.status
      || responseData.data.response.data.statusCode
    ) {
    case 200:
      if (isEdit) {
        setRows(prevRows => {
          return prevRows.map(row => {
            if (row.id === responseData.data.skillMapId) {
              return {
                ...row,
                cycleName: performanceYearValue,
                startDate: startDateValue,
                endDate: endDateValue,
                lastUpdatedDate: new Date(),
              };
            }

            return row;
          });
        });
      } else {
        updatePerformanceCycleList();
      }
      snackbarOpen(
        isEdit
          ? ADD_EDIT_RESPONSE_MESSAGES.PERFORMANCE_PERIOD_UPDATE_SUCCESS
          : ADD_EDIT_RESPONSE_MESSAGES.PERFORMANCE_PERIOD_ADD_SUCCESS,
        'success',
      );
      handleCloseWithReset();
      break;
    case 400:
      snackbarOpen(
        responseData.data.response.data.externalMessage,
        'warning',
      );
      break;
    case 401:
      snackbarOpen(AUTHENTICATION_FAILED, 'warning');
      break;
    case 500:
      snackbarOpen(ADD_EDIT_RESPONSE_MESSAGES.OOPS_TRY_AGAIN, 'warning');
      handleCloseWithReset();
      break;
    default:
      snackbarOpen(
        isEdit
          ? ADD_EDIT_RESPONSE_MESSAGES.UNABLE_TO_UPDATE_PERFORM_CYCLE
          : ADD_EDIT_RESPONSE_MESSAGES.UNABLE_TO_ADD_PERFORM_CYCLE,
        'warning',
      );
      handleCloseWithReset();
      break;
    }
  };

  const validateFields = (performanceData) => {
    let isValid = true;

    if (!performanceData.cycleName) {
      setPerformanceYearError(
        ADD_EDIT_RESPONSE_MESSAGES.ENTER_PERFORMANCE_YEAR_NAME,
      );
      isValid = false;
    } else {
      setPerformanceYearError('');
    }

    if (!performanceData.startDate) {
      setStartDateError(ADD_EDIT_RESPONSE_MESSAGES.ENTER_START_DATE);
      isValid = false;
    } else {
      setStartDateError('');
    }

    if (!performanceData.endDate) {
      setEndDateError(ADD_EDIT_RESPONSE_MESSAGES.ENTER_END_DATE);
      isValid = false;
    } else {
      setEndDateError('');
    }

    return isValid;
  };

  const submitAction = async (performanceInfo, isUpdate = false) => {
    try {
      const trimmedPerformanceYearValue = performanceYearValue.trim();

      if (performanceYearError || startDateError || endDateError) return;

      const requestData = {
        ...performanceInfo,
        cycleName: trimmedPerformanceYearValue,
        startDate: startDateValue,
        endDate: endDateValue,
        lastUpdatedBy,
      };

      if (!validateFields(requestData)) return;

      if (isEdit) isEditMode(true);

      const response = isUpdate
        ? await UseAxios(updatePerformancePeriod(requestData))
        : await UseAxios(addPerformancePeriod(requestData));

      handleResponse(response);
      setAutoFocus(false);
    } catch (error) {
      snackbarOpen(
        isEdit
          ? ADD_EDIT_RESPONSE_MESSAGES.UNABLE_TO_UPDATE_PERFORM_CYCLE
          : ADD_EDIT_RESPONSE_MESSAGES.UNABLE_TO_ADD_PERFORM_CYCLE,
        'warning',
      );
      handleCloseWithReset();
      console.error('Error:', error);
    }
  };

  const handleSubmit = () => submitAction({});

  const handleUpdate = () => submitAction({ performanceId: data?.id }, true);

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setPerformanceYearValue(newValue);
    setAutoFocus(false);

    const specialCharPattern = /[^a-zA-Z0-9 ]/;

    if (specialCharPattern.test(newValue)) {
      setPerformanceYearError(
        `${ADD_EDIT_RESPONSE_MESSAGES.PERFORM_CYCLE_NO_SPECIAL_CHAR}`,
      );
    } else if (newValue.length < 3 || newValue.length > 255) {
      setPerformanceYearError(
        `${ADD_EDIT_RESPONSE_MESSAGES.PERFORMANCE_CYCLE_NAME_MUST_BE}`,
      );
    } else {
      setPerformanceYearError('');
    }
  };

  const handleStartDateChange = ({ value }) => {
    const formattedDate = dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    const currentDay = dayjs().format('YYYY-MM-DD');
    const maxDate = dayjs(currentDate).add(100, 'year')
      .format('YYYY-MM-DD');

    setStartDateValue(formattedDate);

    // Validate start date
    let startDateErrorMessage = '';
    let endDateErrorMessage = '';

    if (!formattedDate) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.ENTER_START_DATE;
    } else if (!isEdit && endDateValue && (new Date(endDateValue) < new Date(currentDay)
      || new Date(endDateValue) >= new Date(maxDate))) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.SELECT_VALID_DATE_RANGE;
    } else if (!isEdit && (new Date(formattedDate) < new Date(currentDay)
      || new Date(formattedDate) >= new Date(maxDate))) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.SELECT_VALID_DATE_RANGE;
    } else if (new Date(formattedDate) >= new Date(endDateValue)) {
      startDateErrorMessage
        = ADD_EDIT_RESPONSE_MESSAGES.START_DATE_BEFORE_END_DATE;
    } else {
      setStartDateError('');
      setEndDateError('');
    }
    setStartDateError(startDateErrorMessage);
    setEndDateError(endDateErrorMessage);
  };

  const handleEndDateChange = ({ value }) => {
    const formattedDate = dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    const currentDay = dayjs().format('YYYY-MM-DD');
    const maxDate = dayjs(currentDate).add(100, 'year')
      .format('YYYY-MM-DD');
    setEndDateValue(formattedDate);

    // Validate end date
    let endDateErrorMessage = '';
    let startDateErrorMessage = '';

    if (!formattedDate) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.ENTER_END_DATE;
    } else if (!isEdit && startDateValue && (new Date(startDateValue) < new Date(currentDay)
      || new Date(startDateValue) >= new Date(maxDate))) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.SELECT_VALID_DATE_RANGE;
    } else if (!isEdit && (new Date(formattedDate) < new Date(currentDay)
      || new Date(formattedDate) >= new Date(maxDate))) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.SELECT_VALID_DATE_RANGE;
    } else if (new Date(startDateValue) >= new Date(formattedDate)) {
      endDateErrorMessage
        = ADD_EDIT_RESPONSE_MESSAGES.START_DATE_BEFORE_END_DATE;
    } else {
      setStartDateError('');
      setEndDateError('');
    }
    setEndDateError(endDateErrorMessage);
    setStartDateError(startDateErrorMessage);
  };

  return (
    <div>
      <FormDialog
        title={
          isEdit
            ? ADD_EDIT_DIALOG.EDIT_PERFORMANCE_PERIOD
            : ADD_EDIT_DIALOG.ADD_PERFORMANCE_PERIOD
        }
        open={modalOpen}
        button1Action={handleCloseWithReset}
        dialogPaperCss="add-performance-cycle-dialog-paper"
        dialogCss="performance-cycle-dialog"
        dialogBodycontent={
          <div className="performance-cycle-dialog-body">
            <CommonTextField
              textCss="performance-cycle-name"
              label={ADD_EDIT_DIALOG.PERFORMANCE_YEAR_NAME}
              value={performanceYearValue}
              onChange={handleInputChange}
              placeholder={ADD_EDIT_DIALOG.PERFORMANCE_YEAR_PLACEHOLDER}
              autoFocus={true}
              size="small"
              id={
                isEdit
                  ? 'edit-performance-cycle-name-text'
                  : 'add-performance-cycle-name-text'
              }
            />
            {performanceYearError && (
              <div
                className={
                  isEdit
                    ? 'edit-performance-cycle-error'
                    : 'performance-cycle-error'
                }
              >
                <small>{performanceYearError}</small>
              </div>
            )}
            <div className="performance-cycle-date-picker">
              <div className="performance-cycle-date-picker-start-date">
                <CommonDatePicker
                  textCss="performance-cycle-start-date"
                  label={ADD_EDIT_DIALOG.PERFORMANCE_START_DATE}
                  minDate={isEdit ? dayjs('01-01-2000') : dayjs(currentDate)}
                  maxDate={
                    isEdit
                      ? dayjs('31-12-2099')
                      : dayjs(currentDate).add(100, 'year')
                  }
                  placeholder={ADD_EDIT_DIALOG.PERFORMANCE_START_PLACEHOLDER}
                  autoFocus={autoFocus}
                  value={startDateValue ? dayjs(startDateValue) : null}
                  onChange={(date) =>
                    handleStartDateChange({
                      name: 'startDate',
                      value: date,
                    })
                  }
                  id={isEdit ? 'edit-start-date-text' : 'add-start-date-text'}
                  className="custom-calender"
                />
                {startDateError && (
                  <div
                    className={
                      isEdit
                        ? 'edit-performance-cycle-error'
                        : 'performance-cycle-error'
                    }
                  >
                    <small>{startDateError}</small>
                  </div>
                )}
              </div>
              <div className="performance-cycle-date-picker-end-date">
                <CommonDatePicker
                  textCss="performance-cycle-end-date"
                  label={ADD_EDIT_DIALOG.PERFORMANCE_END_DATE}
                  minDate={isEdit ? dayjs('01-01-2000') : dayjs(currentDate)}
                  maxDate={
                    isEdit
                      ? dayjs('31-12-2099')
                      : dayjs(currentDate).add(100, 'year')
                  }
                  placeholder={ADD_EDIT_DIALOG.PERFORMANCE_END_PLACEHOLDER}
                  autoFocus={autoFocus}
                  value={endDateValue ? dayjs(endDateValue) : null}
                  onChange={(date) =>
                    handleEndDateChange({
                      name: 'endDate',
                      value: date,
                    })
                  }
                  id={isEdit ? 'edit-end-date-text' : 'add-end-date-text'}
                  className="custom-calender"
                />
                {endDateError && (
                  <div
                    className={
                      isEdit
                        ? 'edit-performance-cycle-error'
                        : 'performance-cycle-error'
                    }
                  >
                    <small>{endDateError}</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        }
        button1={CANCEL_TEXT}
        button2={isEdit ? UPDATE : SAVE}
        button2Action={isEdit ? handleUpdate : handleSubmit}
        cancelButtonId={
          isEdit
            ? 'edit-performance-cycle-cancel-btn'
            : 'add-performance-cycle-cancel-btn'
        }
        submitButtonId={
          isEdit
            ? 'edit-performance-cycle-submit-btn'
            : 'add-performance-cycle-submit-btn'
        }
      />
    </div>
  );
};

AddEditPerformanceCycleForm.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  lastUpdatedBy: PropTypes.string,
  updatePerformanceCycleList: PropTypes.func.isRequired,
  setRows: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  isEditMode: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default AddEditPerformanceCycleForm;
